import React from "react";
import { ArrowRight } from "lucide-react";
import { IconButton } from "@/stories/icon-button/icon-button";
import { ButtonSize, ButtonVariant } from "@/stories/button/button";
// import { initBranch, trackEvent } from "@/app/utils/branchio";
import { setAnalyticsEvent } from "@/app/utils/SetAnalyticsEvent";
import { useRouter } from "next/navigation";
import { useCleverTapEvents } from "@/app/hooks/use-clevertap";

interface FDListingCardProps {
    fdName: string;
    fdLogo: string;
    interestRate: number;
    tagLine?: string;
    id: string;
    productType: string;
    product: string;
}

const FDListingCard: React.FC<FDListingCardProps> = ({
    fdName,
    fdLogo,
    interestRate,
    tagLine,
    id,
    product,
}) => {
    const router = useRouter();
    const { cleverTapTrackEvent } = useCleverTapEvents();

    const handleCardClick = async (product: string, fdName: string) => {
        try {
            setAnalyticsEvent(window.location.origin, `FD_Product_Selection_${product}`);
            cleverTapTrackEvent(`FD_Product_Selection_${product}`, {});
            // await initBranch();
            // trackEvent(`FD_Product_Selection_${product}`, "", "");
        } catch (error) {
            console.error("Error initializing Branch:", error);
        } finally {
            router.push(`fd/${fdName.split(" ").join("-").toLowerCase()}`);
            //   window.location.href = `${process.env.ICM_FD_URL + DirectPaths.FD_POSTFIX_PATH}/${productType}/${product}`;
        }
    };

    return (
        <div
            id={`fd-card-${id}`}
            onClick={() => handleCardClick(product, fdName)}
            key={id}
            className="border-neutralOpacityLight-8 relative flex w-full cursor-pointer flex-col items-center justify-between gap-s7 rounded-lg border px-s7 pb-s7 pt-s11 md:flex-row md:gap-6 md:p-6"
        >
            {tagLine?.length && (
                <div
                    id={`fd-tagline-${id}`}
                    title={tagLine}
                    className="absolute left-s7 top-[5px] max-h-[27px] w-[78%] -translate-y-1/2 truncate rounded-lg bg-green-100 px-1 py-1 text-xs font-medium leading-5 text-success-700 md:left-6 md:top-0 md:w-auto md:px-2"
                >
                    {tagLine}
                </div>
            )}
            {/* Left Section */}
            <div
                id={`fd-left-section-${id}`}
                className="flex flex-grow flex-col items-center gap-4 md:flex-row"
            >
                <div
                    id={`fd-logo-container-${id}`}
                    className="relative mb-4 h-[56px] w-full flex-shrink-0 md:mb-0 md:h-[72px] md:w-[72px]"
                >
                    <div className="flex h-full w-full items-start overflow-hidden md:rounded-md md:border md:border-gray-200">
                        <img
                            id={`fd-logo-${id}`}
                            src={fdLogo}
                            alt={`${fdName} Logo`}
                            className="h-full p-0 md:p-1"
                        />
                    </div>
                </div>

                <div
                    id={`fd-name-container-${id}`}
                    className="flex h-full flex-grow items-start border-r-0 md:items-center md:border-r md:border-dashed md:border-gray-200"
                >
                    <p
                        id={`fd-name-${id}`}
                        className="text-ellipsis pr-4 text-base font-semibold leading-snug text-gray-900"
                    >
                        {fdName}
                    </p>
                </div>
            </div>
            {/* Right Section */}
            <div
                id={`fd-right-section-${id}`}
                className="flex w-full flex-col items-start gap-4 md:w-[155px] md:flex-row md:items-center"
            >
                <div id={`fd-interest-container-${id}`} className="w-[103px] text-left">
                    <p id={`fd-interest-text-${id}`} className="text-xs leading-3 text-gray-500">
                        upto
                    </p>
                    <p
                        id={`fd-interest-rate-${id}`}
                        className="my-1 text-5xl font-semibold leading-7 tracking-tight"
                    >
                        {Number(interestRate).toFixed(2)}
                        <sup className="top-[-1.5em] text-sm">%</sup>
                    </p>
                    <p
                        id={`fd-interest-per-annum-${id}`}
                        className="text-xs leading-3 text-gray-500"
                    >
                        per annum
                    </p>
                </div>
                <IconButton
                    id={`fd-action-btn-${id}`}
                    icon={<ArrowRight />}
                    size={ButtonSize.Small}
                    variant={ButtonVariant.SecondaryDefault}
                />
            </div>
        </div>
    );
};

export default FDListingCard;
